/**
 * Shared post-submission success screen (design taken from Mark Attendance).
 * Show it only after the backend confirms the save; ``onComplete`` runs once
 * after ``delayMs`` (navigate there, or dismiss for pages that stay open).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { MESSAGE_DURATION_MS, useSecondsLeft } from "../../hooks/useAutoHide";
import MessageCountdown from "./MessageCountdown";

/** Circular green/cyan badge with the white check. */
export function SuccessMark() {
  return (
    <div className="relative mb-5">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-200/60 animate-bounce" style={{ animationDuration: "1.5s" }}>
        <CheckCircle2 size={38} className="text-white drop-shadow" />
      </div>
      <div className="absolute -inset-2 rounded-full border-4 border-emerald-300/50 animate-ping" style={{ animationDuration: "2s" }} />
    </div>
  );
}

export function SuccessTitle({ as: Tag = "p", children, className = "" }) {
  return (
    <Tag className={`text-xl font-extrabold bg-gradient-to-r from-emerald-700 via-teal-600 to-cyan-600 bg-clip-text text-transparent ${className}`}>
      {children}
    </Tag>
  );
}

const VARIANT_CLASS = {
  // Covers the (relatively positioned) form card it sits in.
  overlay:
    "absolute inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-emerald-50/95 via-white/95 to-teal-50/95 backdrop-blur-sm rounded-3xl",
  // Takes the place of a form card.
  panel:
    "relative flex flex-col items-center justify-center min-h-[340px] w-full px-6 py-12 text-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-100 shadow-sm rounded-3xl",
};

export default function SuccessScreen({
  title,
  message,
  redirecting = true,
  delayMs = MESSAGE_DURATION_MS,
  onComplete,
  variant = "overlay",
  className = "",
}) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Runs once: when the countdown ends, or earlier when the user clicks close.
  const firedRef = useRef(false);
  const finish = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onCompleteRef.current?.();
  }, []);

  const [endAt] = useState(() => Date.now() + delayMs);
  const secondsLeft = useSecondsLeft(endAt, Math.ceil(delayMs / 1000));

  useEffect(() => {
    const timer = setTimeout(finish, delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, finish]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`${VARIANT_CLASS[variant] || VARIANT_CLASS.overlay} ${className}`}
    >
      <MessageCountdown seconds={secondsLeft} onClose={finish} className="absolute top-3 right-3" />
      <SuccessMark />
      <SuccessTitle className="text-center">{title}</SuccessTitle>
      {message ? <p className="text-sm text-slate-500 mt-2 font-medium text-center">{message}</p> : null}
      {redirecting ? (
        <div className="mt-4 flex gap-1.5" aria-hidden>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0s" }} />
          <span className="w-2.5 h-2.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: "0.15s" }} />
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: "0.3s" }} />
        </div>
      ) : null}
    </div>
  );
}
